// GERADO de backend-senne/src/contracts — NÃO editar; rode scripts/sync-contracts.sh

import { z } from 'zod'
import { scopeSchema } from './scope'

/** Uma permissão efetiva: código + onde vale. `codigo` é string; o catálogo abaixo é o vocabulário ativo. */
export const effectivePermissionSchema = z.object({
  codigo: z.string(),
  scopeType: scopeSchema,
  scopeId: z.number().optional(),
})
export type EffectivePermission = z.infer<typeof effectivePermissionSchema>

/** Grant/deny direto (user_permissao). */
export const permissionGrantSchema = effectivePermissionSchema.extend({
  effect: z.enum(['grant', 'deny']).default('grant'),
})
export type PermissionGrant = z.infer<typeof permissionGrantSchema>

type CatalogEntry = {
  codigo: string
  descricao: string
  defaultScope: z.infer<typeof scopeSchema>
  legacyIds: number[] // permissoes.id que fazem backfill pra este codigo ([] = não vem de permissoes)
}

/**
 * ADR-0009 (2026-09-25): a pessoa não tem papel. O TIPO de pessoa que o papel carregava vira código de acesso (qual
 * portal/fluxo ela usa). Sem `legacyIds`: vêm do `person_role`, não de `permissoes` (spec 042).
 */
const ACCESS_CATALOG_ENTRIES: readonly CatalogEntry[] = [
    {
        codigo: 'portal:hospital',
        descricao: 'Acessar o portal como equipe do hospital',
        defaultScope: 'HOSPITAL',
        legacyIds: [],
    },
    {
        codigo: 'portal:patient',
        descricao: 'Acessar o portal como paciente',
        defaultScope: 'SELF',
        legacyIds: [],
    },
    {
        codigo: 'portal:doctor',
        descricao: 'Acessar o portal como médico prescritor',
        defaultScope: 'SELF',
        legacyIds: [],
    },
    {
        codigo: 'portal:internal-doctor',
        descricao: 'Acessar o portal como médico interno',
        defaultScope: 'GLOBAL',
        legacyIds: [],
    },
    {
        codigo: 'portal:convenio',
        descricao: 'Acessar o portal como convênio',
        defaultScope: 'CONVENIO',
        legacyIds: [],
    },
]

/**
 * Vocabulário ATIVO (rationalize 2026-06-22, aterrado no uso real — workflow w7szduykg).
 * `nivel` legado é lixo (ignorado). `admin:full` deriva do papel ADMIN (ROLE_OPERATION, role_id=1), não de permissoes.
 * permissoes.id 1 = "admin de hospital" no backend → users:manage @ HOSPITAL.
 */
export const PERMISSION_CATALOG: readonly CatalogEntry[] = [
  { codigo: 'admin:full', descricao: 'Acesso total (superadmin/operação)', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'users:manage', descricao: 'Gerir usuários e aprovações no hospital', defaultScope: 'HOSPITAL', legacyIds: [1] },
  { codigo: 'scheduling:manage', descricao: 'Agendar e gerir coletas', defaultScope: 'HOSPITAL', legacyIds: [2] },
  { codigo: 'results:read', descricao: 'Ver resultados (escopo decide próprios/terceiros)', defaultScope: 'SELF', legacyIds: [3] },
  { codigo: 'reports:read', descricao: 'Acessar relatórios (quais via user_report)', defaultScope: 'HOSPITAL', legacyIds: [6] },
  // Spec 008. Sem `legacyIds`: são direitos NOVOS, sem equivalente no esquema numérico legado.
  // Separados de propósito (FR-016): ver erro técnico não pode implicar ver quem acessou dado de
  // paciente. `audit:read` é HOSPITAL porque a trilha é recortada por hospital; `observability:read`
  // é GLOBAL porque erro de aplicação não pertence a hospital nenhum.
  { codigo: 'audit:read', descricao: 'Consultar a trilha de acesso a dado de paciente', defaultScope: 'HOSPITAL', legacyIds: [] },
  { codigo: 'observability:read', descricao: 'Consultar erros de aplicação', defaultScope: 'GLOBAL', legacyIds: [] },
    ...ACCESS_CATALOG_ENTRIES,
  // P20EE-115. Direito NOVO (sem `legacyIds`), fundação das telas /erp/*. Sem grant: a Operação tem `admin:full`.
  { codigo: 'erp:operate', descricao: 'Operar as telas do ERP', defaultScope: 'GLOBAL', legacyIds: [] },
  // Spec 20261015-160924: the ERP tree (`./erp-tree.ts`), one code per category, page and tab. New rights, GLOBAL.
  { codigo: 'erp:agenda', descricao: 'Ver a categoria Agenda do ERP', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'erp:carlao', descricao: 'Ver a categoria Carlão do ERP', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'erp:carlao:config', descricao: 'Configurar o Carlão no ERP', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'erp:carlao:config:recusas', descricao: 'Configurar os exames que não realizamos', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'erp:carlao:config:regras', descricao: 'Configurar o acréscimo por regra', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'erp:carlao:config:rotinas', descricao: 'Configurar as rotinas do Carlão', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'erp:carlao:config:sinonimos', descricao: 'Configurar os sinônimos de exame', defaultScope: 'GLOBAL', legacyIds: [] },
  // Spec 20261028-133000 AC-10: the "Ignorar" tab.
  { codigo: 'erp:carlao:config:ignorar', descricao: 'Configurar os termos que o Carlão ignora', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'erp:carlao:analytics', descricao: 'Ver o Analytics do Carlão no ERP', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'erp:carlao:analytics:analises', descricao: 'Ver as análises do Carlão', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'erp:carlao:analytics:skills', descricao: 'Gerir as skills do Carlão', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'erp:sistema', descricao: 'Ver a categoria Sistema do ERP', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'erp:sistema:observabilidade', descricao: 'Ver a Observabilidade no ERP', defaultScope: 'GLOBAL', legacyIds: [] },
  // P20EE-196. The impersonation allowlist on the core (migration 0053): the legacy flag `users.can_impersonate` lives
  // where a person who exists only in the core has no row. An exact grant, never implied by `admin:full` (spec 004
  // FR-001: admin is necessary, not sufficient).
  { codigo: 'admin:impersonate', descricao: 'Ver o portal como outra pessoa', defaultScope: 'GLOBAL', legacyIds: [] },
  // Spec 20261028-134000 AC-2: the ERP's Sistema › API page and the OpenAPI document behind it.
  { codigo: 'api:docs', descricao: 'Ver a documentação da API', defaultScope: 'GLOBAL', legacyIds: [] },
  // Spec 20261105-090000 AC-1: Financeiro › Notas fiscais, the upload of a patient's invoice.
  { codigo: 'erp:financeiro', descricao: 'Ver a categoria Financeiro do ERP', defaultScope: 'GLOBAL', legacyIds: [] },
  { codigo: 'erp:financeiro:notas', descricao: 'Enviar e remover notas fiscais de paciente', defaultScope: 'GLOBAL', legacyIds: [] },
]

/** Permissões fora do vocabulário ativo e não enforçadas no modelo novo, MAS com atribuições reais em user_permissao
 * (~420 grants; id5=273) e provavelmente enforçadas no legado backend-portal — por isso são RETIDAS (linhas preservadas,
 * não removidas); mapeamento revisitado no sunset do legado. */
export const LEGACY_RETAINED_PERMISSION_IDS = [4, 5, 7, 8, 9, 10, 11] as const

/** permissoes.id 12 (Médico Interno) não é permissão atômica — vira marcador do papel MEDICO_INTERNO. */
export const ROLE_MARKER_LEGACY_PERMISSION_IDS = [12] as const
